// @flow
import * as React from 'react';
import { memo, useMemo } from 'react';
import { ButtonBase, Typography } from '@mui/material';
import { colorByStatus, labelByStatus, Portal } from '../../../../../shared';
import { StatusDto } from '@coverright/data-access/types/enrollment';
import { useStatusForm, withStatusForm } from '../../../../../features';
import { useClient } from '../../../hooks/client-context';

const Progress = memo(({status}: { status?: StatusDto }) => {
  const client= useClient();
  const chain = useMemo(() => getChainByStatus(status), [status]);
  const {open} = useStatusForm();
  return (
    <ButtonBase sx={{flex: 1}} onClick={() => open(client)}>
      <Portal direction={'row'} gap={2}
             flex={1} justifyContent={'space-between'}
              sx={{p: '8px 16px',}}
             alignItems={'center'}>
        {chain.map(item => <Item key={item.status} {...item} />)}
      </Portal>
    </ButtonBase>
  );
})

export const StatusProgress = withStatusForm(Progress)

const Item = ({status, active}: {status?: StatusDto, active: boolean}) => (
  status ? <Typography variant={'body2'} color={active ? colorByStatus(status) : '#b9b9b9'} align={'left'}
              noWrap sx={{borderBottom: `4px solid ${active ? colorByStatus(status) : '#dadada'}`, pb: .5, flexGrow: 1 }}>
    <b>{labelByStatus(status)}</b>
  </Typography> : null
)

const getChainByStatus = (status?: StatusDto): {status: StatusDto, active: boolean}[] => {
  const noActive = [
    {status: StatusDto.Attempted, active: false},
    {status: StatusDto.FollowUp, active: false},
    {status: StatusDto.SalesQualified, active: false},
  ]
  switch (status) {
    case StatusDto.NotYetEngaged: return [
      {status: StatusDto.NotYetEngaged, active: true},
      ...noActive
    ];
    case StatusDto.OutOfService: return [
      {status: StatusDto.OutOfService, active: true},
      ...noActive
    ];
    case StatusDto.Test: return [
      {status: StatusDto.Test, active: true},
      ...noActive
    ];
    case StatusDto.Duplicated: return [
      {status: StatusDto.Duplicated, active: true},
      ...noActive
    ];
    case StatusDto.Attempted: return [
      {status: StatusDto.NotYetEngaged, active: false},
      {status: StatusDto.Attempted, active: true},
      {status: StatusDto.FollowUp, active: false},
      {status: StatusDto.SalesQualified, active: false},
    ];
    case StatusDto.FollowUp: return [
      {status: StatusDto.NotYetEngaged, active: false},
      {status: StatusDto.Attempted, active: false},
      {status: StatusDto.FollowUp, active: true},
      {status: StatusDto.SalesQualified, active: false},
    ];
    case StatusDto.Contacted: return [
      {status: StatusDto.Attempted, active: false},
      {status: StatusDto.FollowUp, active: false},
      {status: StatusDto.Contacted, active: true},
      {status: StatusDto.SalesQualified, active: false},
    ];
    case StatusDto.SalesQualified: return [
      {status: StatusDto.Attempted, active: false},
      {status: StatusDto.FollowUp, active: false},
      {status: StatusDto.SalesQualified, active: true},
      {status: StatusDto.ApplicationSubmitted, active: false},
    ];
    case StatusDto.Disenrolled: return [
      {status: StatusDto.NotYetEngaged, active: false},
      {status: StatusDto.Attempted, active: false},
      {status: StatusDto.FollowUp, active: false},
      {status: StatusDto.Disenrolled, active: true},
    ];
    case StatusDto.Disqualified: return [
      {status: StatusDto.NotYetEngaged, active: false},
      {status: StatusDto.Attempted, active: false},
      {status: StatusDto.FollowUp, active: false},
      {status: StatusDto.Disqualified, active: true},
    ];
    case StatusDto.ApplicationSubmitted: return [
      {status: StatusDto.Attempted, active: false},
      {status: StatusDto.FollowUp, active: false},
      {status: StatusDto.SalesQualified, active: false},
      {status: StatusDto.ApplicationSubmitted, active: true},
    ];
    default: return [
      {status: StatusDto.NotYetEngaged, active: false},
      ...noActive
    ]
  }
}
